import { Dialog, Transition } from '@headlessui/react'
import { useFormik } from 'formik'
import React, { Fragment, useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import { IoMdClose } from 'react-icons/io'
import * as Yup from 'yup'
import { WalletSymbol } from '../svgs/WalletSymbol'

import jwtDecode from 'jwt-decode'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../app/store'
import {
  selectCurrentToken,
  selectCurrentUser,
} from '../../../features/auth/authSlice'
import {
  useEstimateFeeMutation,
  useSellBtcMutation,
} from '../../../features/bitcoin-wallet/bitcoinWalletSlice'
import { useGetBitcoinRateQuery, useGetUsdtRateQuery } from '../../../features/trade&coins/bitcoinSlice'
import { useGetCoinsQuery } from '../../../features/trade&coins/coinSlice'
import {
  openModal
} from '../../../features/two-factor-modal/twoFactorModalSlice'
import { Loader } from '../../../shared/Loader'
import { SuccessIcon } from '../../../shared/SuccessIcon'
import { useEstimateUsdtFeeMutation, useSellUsdtMutation } from '../../../features/tron-wallet/tronWalletSlice'

interface ISellViaWallet {
  is2FAAuthenticated: boolean
  setIs2FAAuthenticated: React.Dispatch<React.SetStateAction<boolean>>
}

export const SellUSDTViaWallet = ({
  is2FAAuthenticated,
  setIs2FAAuthenticated,
}: ISellViaWallet) => {
  const [isSellModalOpen, setIsSellModalOpen] = useState(false)
  const dispatch = useDispatch()
  const user = useSelector(selectCurrentUser)
  const token = useSelector(selectCurrentToken)
  const [usdValue, setUsdValue] = useState<number>(0)

  const is2FAModalOpen = useSelector(
    (state: RootState) => state.twoFactorModal.isOpen
  )

  const [currentStep, setCurrentStep] = useState<'Sell' | 'Success'>('Success')
  const [change, setChange] = useState<boolean>(false)

  useEffect(() => {
    let timeout: any
    if (!isSellModalOpen) {
      timeout = setTimeout(() => {
        setCurrentStep('Sell')
      }, 500)
    }
    return () => timeout && clearTimeout(timeout)
  }, [isSellModalOpen])

  const [
    estimate,
    { isLoading: isEstimateLoading, isSuccess, data: estimateData },
  ] = useEstimateUsdtFeeMutation()
  const [sellUsdt, { isSuccess: isSellSuccess }] = useSellUsdtMutation()
  const { data: usdtRate, isLoading: isUsdtRateLoading } =
    useGetUsdtRateQuery()

  const { data: coins, isLoading: isCoinsLoading } = useGetCoinsQuery()

  const formik = useFormik({
    initialValues: {
      amount: 0,
      note: '',
    },
    validationSchema: Yup.object().shape({
      amount: Yup.number().moreThan(0, 'Amount should be more than 0'),
      note: Yup.string(),
    }),
    onSubmit: async (values) => {
      try {
        const decoded: {
          iat: number
          exp: number
          is2FAAuthenticated?: boolean
          userId: string
        } = jwtDecode(token!)
        if (
          user?.is2FAEnabled &&
          (!decoded.is2FAAuthenticated ||
            decoded.exp < new Date().getTime() / 1000)
        ) {
          dispatch(openModal())
        } else {
          toast.loading('Sell USDT on progress...')
          await sellUsdt({
            amount: values.amount,
            note: values.note,
          })
          formik.resetForm()
          toast.dismiss()
          setCurrentStep('Success')
        }
      } catch (error: any) {
        toast.dismiss()
        toast.error(error?.data?.message)
        console.log(error)
      }
    },
  })

  const handleNext = async () => {
    try {
      setChange(false)
      await estimate({
        amount: formik.values.amount,
        toAddress: coins.find((x: any) => x.name === 'usdt')?.paymentAddress,
      }).unwrap()

      if (estimateData?.error?.data.message) {
        toast.dismiss()
        toast.error(estimateData?.error?.data.message)
        return
      }
    } catch (error: any) {
      toast.dismiss()
      toast.error(error?.data.message)
    }
  }

  const calculateUsdValue = (value: number) => {
    const usdValue = (value * usdtRate?.usd).toFixed(2)
    setUsdValue(Number(usdValue))
  }

  function _renderStepContent(step: string) {
    switch (step) {
      case 'Sell':
        return (
          <>
            <div className='flex items-center justify-between px-4 pt-2 pb-4'>
              <div className='flex items-center gap-3'>
                <div className='box-content p-2.5 rounded-full bg-primary'>
                  <svg
                    width='28'
                    height='28'
                    viewBox='0 0 28 28'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      d='M27.4516 14.0609C27.1133 13.7226 26.5533 13.7226 26.215 14.0609L25.6783 14.5976V14.0026C25.6783 7.56255 20.44 2.32422 14 2.32422C10.4416 2.32422 7.11663 3.92255 4.88829 6.69922C4.58496 7.07255 4.64329 7.63255 5.02829 7.93589C5.40163 8.22755 5.94996 8.16922 6.25329 7.79589C8.15496 5.42755 10.9783 4.07422 14 4.07422C19.4716 4.07422 23.9283 8.53089 23.9283 14.0026V14.5859L23.3916 14.0492C23.0533 13.7109 22.4933 13.7109 22.155 14.0492C21.8166 14.3876 21.8166 14.9476 22.155 15.2859L24.185 17.3159C24.2666 17.3976 24.36 17.4559 24.465 17.5026C24.57 17.5492 24.6866 17.5726 24.8033 17.5726C24.92 17.5726 25.025 17.5492 25.1416 17.5026C25.2466 17.4559 25.34 17.3976 25.4216 17.3159L27.4516 15.2859C27.79 14.9592 27.79 14.3992 27.4516 14.0609Z'
                      fill='black'
                    />
                    <path
                      d='M22.9715 20.0624C22.5982 19.7591 22.0382 19.8174 21.7465 20.2024C19.8448 22.5707 17.0215 23.9241 13.9999 23.9241C8.52818 23.9241 4.07152 19.4674 4.07152 13.9957V13.4124L4.60818 13.9491C4.78318 14.1241 5.00485 14.2057 5.22652 14.2057C5.44818 14.2057 5.66985 14.1124 5.83318 13.9374C6.17152 13.5991 6.17152 13.0391 5.83318 12.7007L3.81485 10.6707C3.73318 10.6007 3.62818 10.5307 3.52318 10.4841C3.31318 10.3907 3.06818 10.3907 2.85818 10.4841C2.75318 10.5307 2.65985 10.6007 2.57818 10.6707L0.548184 12.7007C0.20985 13.0391 0.20985 13.5991 0.548184 13.9374C0.886517 14.2757 1.44652 14.2757 1.78485 13.9374L2.32152 13.4007V13.9957C2.32152 20.4357 7.55985 25.6741 13.9999 25.6741C17.5582 25.6741 20.8832 24.0757 23.1115 21.2991C23.4148 20.9257 23.3565 20.3657 22.9715 20.0624Z'
                      fill='black'
                    />
                    <path
                      d='M18.025 13.58C18.34 13.1133 18.515 12.565 18.515 11.9583C18.515 10.4067 17.15 9.04167 15.5983 9.04167H14.6417V7.875C14.6417 7.39667 14.245 7 13.7667 7C13.2883 7 12.8917 7.39667 12.8917 7.875V9.04167H10.5C10.0217 9.04167 9.625 9.43833 9.625 9.91667V14L9.625 18.0833C9.625 18.5617 10.0217 18.9583 10.5 18.9583H12.8917V20.125C12.8917 20.6033 13.2883 21 13.7667 21C14.245 21 14.6417 20.6033 14.6417 20.125V18.9583H16.3333C18.1067 18.9583 19.5417 17.6517 19.5417 16.0417C19.5417 15.0033 18.935 14.0933 18.025 13.58ZM11.375 10.7917H15.5983C16.24 10.7917 16.765 11.4333 16.765 11.9583C16.765 12.6 16.24 13.125 15.5983 13.125H11.375V10.7917ZM16.3333 17.2083H11.375L11.375 14.875H15.5983H16.3333C17.1383 14.875 17.7917 15.4 17.7917 16.0417C17.7917 16.6833 17.1383 17.2083 16.3333 17.2083Z'
                      fill='black'
                    />
                  </svg>
                </div>

                <p className='text-base text-white/40 font-light'>
                  Sell USDT
                </p>
              </div>

              <IoMdClose
                className='text-xl text-white cursor-pointer'
                onClick={() => setIsSellModalOpen(false)}
              />
            </div>

            <hr className='border-white/10' />

            <form
              className='max-w-md mx-auto mt-12'
              onSubmit={formik.handleSubmit}
            >
              <div className='flex flex-col gap-2'>
                <label htmlFor='amount' className='text-sm text-white/40'>
                  Amount(USDT value)
                </label>
                <input
                  id='amount'
                  name='amount'
                  type='number'
                  min='1.00'
                  step='0.01'
                  value={formik.values.amount}
                  onChange={(e) => {
                    setChange(true)
                    calculateUsdValue(Number(e.target.value || 0))
                    formik.handleChange(e)
                  }}
                  placeholder='0.0'
                  className='form-input'
                />
                <p className='text-red-500 text-xs'>
                  {formik.touched.amount &&
                    Boolean(formik.errors.amount) &&
                    formik.errors.amount}
                </p>
              </div>

              <div className='flex flex-col gap-2 mt-4'>
                <label htmlFor='note' className='text-sm text-white/40'>
                  Note (optional)
                </label>
                <textarea
                  name='note'
                  id='note'
                  cols={10}
                  rows={5}
                  onChange={(e) => {
                    setChange(true)
                    formik.handleChange(e)
                  }}
                  value={formik.values.note}
                  className='form-input spin-button-none'
                />

                <p className='text-red-500 text-xs'>
                  {formik.touched.note &&
                    Boolean(formik.errors.note) &&
                    formik.errors.note}
                </p>
              </div>

              {isSuccess && !change && (
                <div className='w-full bg-gray-700 rounded-md border-2 border-dashed border-white/10 p-4 mt-4 flex flex-col items-center gap-3'>
                  <div className='flex flex-col items-center'>
                    <p className='text-xs text-white/40'>Est Fee</p>
                    <p className='text-white text-base font-bold'>
                      {estimateData?.bandwidth || 0} Bandwidth
                    </p>
                    <p className='text-white text-base font-bold'>
                      {estimateData?.energy || 0} Energy
                    </p>
                    <p className='text-white text-base font-bold'>
                      {estimateData?.trx || 0} TRX
                    </p>
                  </div>
                  <svg
                    width='9'
                    height='54'
                    viewBox='0 0 9 54'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      d='M4.85355 0.646447C4.65829 0.451184 4.34171 0.451184 4.14645 0.646447L0.964466 3.82843C0.769204 4.02369 0.769204 4.34027 0.964466 4.53553C1.15973 4.7308 1.47631 4.7308 1.67157 4.53553L4.5 1.70711L7.32843 4.53553C7.52369 4.7308 7.84027 4.7308 8.03553 4.53553C8.2308 4.34027 8.2308 4.02369 8.03553 3.82843L4.85355 0.646447ZM4.14644 53.3536C4.34171 53.5488 4.65829 53.5488 4.85355 53.3536L8.03553 50.1716C8.23079 49.9763 8.23079 49.6597 8.03553 49.4645C7.84027 49.2692 7.52369 49.2692 7.32842 49.4645L4.5 52.2929L1.67157 49.4645C1.47631 49.2692 1.15973 49.2692 0.964464 49.4645C0.769202 49.6597 0.769202 49.9763 0.964464 50.1716L4.14644 53.3536ZM4 1L4 53L5 53L5 1L4 1Z'
                      fill='#00C693'
                    />{' '}
                  </svg>
                  <div className='flex flex-col items-center'>
                    <p className='text-xs text-white/40'>Total amount</p>
                    <p className='text-white text-base font-bold'>
                      {Number(formik?.values?.amount)} USDT + {estimateData?.trx || 0} TRX
                    </p>
                  </div>
                  <svg
                    width='9'
                    height='54'
                    viewBox='0 0 9 54'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      d='M4.85355 0.646447C4.65829 0.451184 4.34171 0.451184 4.14645 0.646447L0.964466 3.82843C0.769204 4.02369 0.769204 4.34027 0.964466 4.53553C1.15973 4.7308 1.47631 4.7308 1.67157 4.53553L4.5 1.70711L7.32843 4.53553C7.52369 4.7308 7.84027 4.7308 8.03553 4.53553C8.2308 4.34027 8.2308 4.02369 8.03553 3.82843L4.85355 0.646447ZM4.14644 53.3536C4.34171 53.5488 4.65829 53.5488 4.85355 53.3536L8.03553 50.1716C8.23079 49.9763 8.23079 49.6597 8.03553 49.4645C7.84027 49.2692 7.52369 49.2692 7.32842 49.4645L4.5 52.2929L1.67157 49.4645C1.47631 49.2692 1.15973 49.2692 0.964464 49.4645C0.769202 49.6597 0.769202 49.9763 0.964464 50.1716L4.14644 53.3536ZM4 1L4 53L5 53L5 1L4 1Z'
                      fill='#00C693'
                    />
                  </svg>
                  <div className='flex flex-col items-center'>
                    <p className='text-xs text-white/40'>You will get(NGN)</p>
                    <p className='text-white text-base font-bold'>
                      {(usdtRate?.ngn * Number((formik.values.amount).toFixed(2))).toFixed(2)}{' '}
                      <span className='text-sm text-white/40'>
                        ({(formik.values.amount).toFixed(2)}USDT)
                      </span>
                    </p>
                  </div>
                </div>
              )}

              {isSuccess && !change ? (
                <button
                  type='submit'
                  className='btn-primary w-full py-2 rounded-lg mt-4'
                >
                  Sell
                </button>
              ) : (
                <button
                  type='button'
                  className='btn-primary w-full py-2 rounded-lg mt-4 flex justify-center items-center gap-2'
                  onClick={handleNext}
                >
                  {isEstimateLoading && <Loader />}
                  Next
                </button>
              )}
            </form>
          </>
        )

      case 'Success':
        return (
          <div className='relative py-24 flex flex-col items-center'>
            <div className='flex flex-col items-center gap-4'>
              <SuccessIcon />
              <p className='text-xl font-light text-white/40'>
                Transaction complete
              </p>
            </div>

            <button
              className='btn-primary py-2 rounded-lg w-full max-w-md mt-12'
              onClick={() => {
                setIsSellModalOpen(false)
              }}
            >
              Done
            </button>
          </div>
        )
    }
  }

  return (
    <div>
      <div
        className='flex flex-col items-center gap-1 cursor-pointer'
        onClick={() => setIsSellModalOpen(true)}
      >
        <div className='border-2 border-white text-white rounded-full box-content p-2'>
          <WalletSymbol size={20} />
        </div>
        <p className='text-white/60 font-light text-[12px]'>Sell via wallet</p>
      </div>

      {!is2FAModalOpen && (
        <Transition appear show={isSellModalOpen} as={Fragment}>
          <Dialog
            as='div'
            className='relative z-50'
            onClose={() => setIsSellModalOpen(false)}
          >
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0'
              enterTo='opacity-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100'
              leaveTo='opacity-0'
            >
              <div className='fixed inset-0 bg-black/40 backdrop-blur-sm' />
            </Transition.Child>

            <div className='fixed inset-0 overflow-y-auto'>
              <div className='flex min-h-full items-center justify-center p-4 text-center'>
                <Transition.Child
                  as={Fragment}
                  enter='ease-out duration-300'
                  enterFrom='opacity-0 scale-95'
                  enterTo='opacity-100 scale-100'
                  leave='ease-in duration-200'
                  leaveFrom='opacity-100 scale-100'
                  leaveTo='opacity-0 scale-95'
                >
                  <Dialog.Panel className='relative font-dashboard w-full max-w-2xl min-h-[500px] transform overflow-hidden rounded-2xl bg-[#161819] px-4 pt-4 pb-10 text-left align-middle shadow-xl transition-all'>
                    {_renderStepContent(currentStep)}
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      )}
    </div>
  )
}
